import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './DocumentListItem.sass'
import Document from 'documents/Document'
import Avatar from 'components/Avatar'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import RadialProgress from 'components/RadialProgress'
import { IconButton } from 'components/IconButton'
import { Id } from 'types/definitions'
import JobDocumentsViewModel from '../../../jobs/views/JobRoot/Job/JobDocumentsViewModel'
import Spinner from 'components/Spinner'
import Pill from 'components/Pill'
import Checkbox from 'components/Checkbox'
import FileTypeIcon from 'components/FileTypeIcon'
import { IconType } from 'utils/icon-util'
import FileButton from 'components/FileButton'

interface Props {
  document: Document
  documentsVM?: JobDocumentsViewModel
  documentClickAction(document: Document): void
  noRead?: boolean
  spacing?: boolean
  selectable?: boolean
  showIcon?: boolean
}

export default observer(function DocumentListItem({
  document,
  documentsVM,
  documentClickAction,
  noRead,
  spacing,
  selectable,
  showIcon = true,
}: Props) {
  const icon = IconType(
    (document.latestVersion && document.latestVersion.files[0].fileName) || '',
  )
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
  })

  const { tags, type } = document
  const tagsToDisplay = 1
  const access = document.accesses.find((a) => a.jobId === documentsVM?.job.id)

  return (
    <div
      className={cx(styles.root, spacing && styles.spacing)}
      data-test={`document-list-item-${document.title}`}
    >
      {selectable && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            checked={documentsVM?.isSelected(document)}
            disabled={document.archived}
            onChange={() => documentsVM?.selectDocument(document)}
          />
        </div>
      )}
      <div
        onClick={() => documentClickAction(document)}
        className={styles.itemContainer}
      >
        {showIcon && (
          <div className={styles.icon}>
            <FileTypeIcon
              height={36}
              primaryColor={icon.color}
              strokeColor="#bcbcbe"
              type={icon.type}
            />
          </div>
        )}
        <div className={styles.info}>
          {document.archived && (
            <div className={styles.archived}>
              <Pill color="red" shape="square" trim>
                Archived
              </Pill>
            </div>
          )}
          {document.latestVersion?.isManualVerificationRequired && (
            <div className={styles.archived}>
              <Pill color="red" shape="square" trim>
                Manual Verification Required
              </Pill>
            </div>
          )}
          <div className={styles.title}>
            <span className={styles.primaryTitle}>{document.title}</span>
            {document.isPurged && <span className={styles.purged}>Purged</span>}
          </div>
          <div className={styles.dateModified}>
            {access && <p>{dateFormatter.format(access.dateModified)}</p>}
          </div>
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <Avatar
                size={20}
                subject={
                  document.latestVersionUnpurged
                    ? document.latestVersionUnpurged.author
                    : document.owner
                }
              />
            </div>
            {(type || tags.length > 0) && (
              <div className={styles.metaItem}>
                <div className={styles.divider} />
              </div>
            )}
            {type && (
              <div className={styles.metaItem}>
                <Pill stout>{type.name}</Pill>
              </div>
            )}
            {!document.latestVersion && !document.uploadProgress && (
              <div className={styles.metaItem}>
                <Pill color="red" trim>
                  Reupload required
                </Pill>
              </div>
            )}
            {tags.length > 0 &&
              tags.map((tag, index) => {
                if (index < tagsToDisplay) {
                  return (
                    <div className={styles.metaItem} key={Id.assert(tag.id)}>
                      <Pill stout>{tag.label}</Pill>
                    </div>
                  )
                } else if (index === tagsToDisplay) {
                  return (
                    <div className={styles.metaItem} key={Id.assert(tag.id)}>
                      <Pill
                        stout
                        title={tags
                          .slice(tagsToDisplay)
                          .map((x) => x.label)
                          .join(', ')}
                      >
                        +{tags.length - index}
                      </Pill>
                    </div>
                  )
                } else {
                  return undefined
                }
              })}
          </div>
        </div>
        <ItemIcon
          document={document}
          documentsVM={documentsVM}
          noRead={noRead}
        />
      </div>
    </div>
  )
})

interface ItemIconProps {
  document: Document
  documentsVM?: JobDocumentsViewModel
  noRead?: boolean
}

const ItemIcon = observer(function ItemIcon({
  document,
  documentsVM,
  noRead,
}: ItemIconProps) {
  if (!document.latestVersion || document.latestVersion.purged) {
    return (
      <span className={styles.status} title="This document must be reuploaded">
        <span className={styles.hover}>
          <FileButton
            onChange={(files: FileList) =>
              documentsVM?.reuploadDocument(document, files[0])
            }
          >
            <IconButton>
              <Icon name="upload" />
            </IconButton>
          </FileButton>
        </span>
      </span>
    )
  }
  if (document.uploadProgress) {
    return (
      <RadialProgress size={24} progress={document.uploadProgress.percent} />
    )
  }
  switch (document.latestVersion && document.latestVersion.state) {
    case 'Pending':
      return (
        <span
          className={styles.status}
          title="This document is waiting for a virus scan to complete"
        >
          <Icon name="pending" />
        </span>
      )
    case 'Scanning':
      return (
        <span
          className={styles.status}
          title="This document is currently undergoing a virus scan"
        >
          <Spinner />
        </span>
      )
    case 'VerifyingClientData':
      return (
        <span
          className={styles.status}
          title="This document is currently undergoing client data verification"
        >
          <Spinner />
        </span>
      )
    case 'ManualVerificationRequired':
      return (
        <span
          className={cx(styles.status, styles.manualVerificationRequired)}
          title={
            document.latestVersion?.manualVerificationReason ===
            'MissingProSignature'
              ? 'The preparer’s signature is missing from this final return'
              : 'The client’s name doesn’t match with the content on the file'
          }
        >
          <Icon name="alert" />
        </span>
      )
    case 'Purged':
      return (
        <span
          className={cx(styles.status, styles.virus)}
          title="This document is infected with a virus and is not available for download"
        >
          <Icon name="virus" />
        </span>
      )
    case 'Failed':
      return (
        <span
          className={cx(styles.status, styles.failed)}
          title="This document has failed to upload"
        >
          <Icon name="info" />
        </span>
      )
  }

  if (document.latestVersion.downloadable && !noRead && documentsVM) {
    return (
      <span className={styles.status}>
        <Dropdown
          width="medium"
          autoDismiss
          position="bottom-end"
          button={
            <span className={styles.hover}>
              <IconButton
                data-test={`download-actions-dropdown-${document.title}`}
              >
                <Icon name="more" />
              </IconButton>
            </span>
          }
        >
          <DropdownTitle>More</DropdownTitle>
          <ViewInlineAction document={document} documentsVM={documentsVM} />
          <DownloadAction document={document} documentsVM={documentsVM} />
          {documentsVM.canRemoveDocs && (
            <DeleteInlineAction document={document} documentsVM={documentsVM} />
          )}
        </Dropdown>
      </span>
    )
  }
  return null
})

const DownloadAction = observer(function DownloadAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  if (!document.latestVersion) {
    return null
  }

  return (
    <div>
      <DropdownItem
        icon={<Icon name="download" />}
        muteClickEvent
        onClick={() => {
          documentsVM.download(document, false)
        }}
      >
        Download
      </DropdownItem>
    </div>
  )
})

const ViewInlineAction = observer(function ViewInlineAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  if (!document.latestVersion) {
    return null
  }

  return (
    <div>
      <DropdownItem
        icon={<Icon name="view" />}
        muteClickEvent
        onClick={() => {
          documentsVM.download(document, true)
        }}
      >
        View
      </DropdownItem>
    </div>
  )
})

const DeleteInlineAction = observer(function DeleteInlineAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  return (
    <div>
      <DropdownItem
        icon={<Icon name="delete" />}
        muteClickEvent
        onClick={() => {
          documentsVM.delete(document)
        }}
      >
        Delete
      </DropdownItem>
    </div>
  )
})
